import type { ComponentType } from 'react'
import { YearsQuestion } from './questions/YearsQuestion'
import { ConnectionQuestion } from './questions/ConnectionQuestion'
import { NameQuestion } from './questions/NameQuestion'
import { ContactQuestion } from './questions/ContactQuestion'
import { VisibilityQuestion } from './questions/VisibilityQuestion'
import { ProgramsQuestion } from './questions/ProgramsQuestion'
import { MemoryQuestion } from './questions/MemoryQuestion'
import type { QuestionProps } from './questions/types'
import type { PhotoCaption } from './PhotoSide'
import { asDraft } from '../../types/registration'
import {
  COMMUNITY,
  ERA_QUOTES,
  ERA_STORIES,
  PANEL_PHOTOS,
  RECENT_ARRIVALS,
  decadeFor,
  membersFromDecade,
} from '../../data/registration'

type Draft = Record<string, unknown>

export interface FlowStep {
  id: number
  eyebrow: string
  question: string
  support?: string
  Form: ComponentType<QuestionProps>
  photo: (data: Draft) => { image: { src: string; alt: string }; caption: PhotoCaption }
}

/**
 * Seven short beats, one question each. The archive answers the first few —
 * it asks nothing of the member — and the community answers the later ones,
 * once their years make that possible.
 */
export const FLOW_STEPS: FlowStep[] = [
  {
    id: 1,
    eyebrow: 'Your years',
    question: 'When were you here?',
    support:
      'Start here. It’s the only thing we need to show you who else was around at the same time.',
    Form: YearsQuestion,
    photo: () => ({
      image: PANEL_PHOTOS.building,
      caption: {
        eyebrow: 'The archive',
        line: 'The building on the corner, before the second floor was rebuilt.',
        stat: { value: COMMUNITY.collectiveYears, label: 'collective years of recovery represented' },
      },
    }),
  },
  {
    id: 2,
    eyebrow: 'Your connection',
    question: 'How were you connected?',
    support: 'You can add more than one later — most people have been more than one thing.',
    Form: ConnectionQuestion,
    photo: (data) => {
      const draft = asDraft(data)
      const era = ERA_STORIES[decadeFor(draft.startYear || '1990')]
      return {
        image: PANEL_PHOTOS.room,
        caption: { eyebrow: era.tag, line: era.headline },
      }
    },
  },
  {
    id: 3,
    eyebrow: 'Your name',
    question: 'What should we call you?',
    support: 'Members only ever see your first name and last initial.',
    Form: NameQuestion,
    photo: (data) => {
      const draft = asDraft(data)
      const decade = decadeFor(draft.startYear || '1990')
      const count = membersFromDecade(draft.startYear || '1990')
      return {
        image: PANEL_PHOTOS.founding,
        caption: {
          eyebrow: `The ${decade}`,
          line:
            count > 0
              ? `${count} people from the ${decade} are already here.`
              : `You’d be the first person here from the ${decade}.`,
        },
      }
    },
  },
  {
    id: 4,
    eyebrow: 'Reaching you',
    question: 'Where can we reach you?',
    support: 'Sign-in only. No newsletters unless you ask for them.',
    Form: ContactQuestion,
    photo: (data) => {
      const draft = asDraft(data)
      const decade = decadeFor(draft.startYear || '1990')
      return {
        image: PANEL_PHOTOS.hands,
        caption: {
          eyebrow: `Recently arrived from the ${decade}`,
          line: 'People who were here when you were.',
          names: RECENT_ARRIVALS[decade] ?? [],
        },
      }
    },
  },
  {
    id: 5,
    eyebrow: 'Your visibility',
    question: 'Who can see you?',
    support:
      'We settle this before asking anything else about your time here, so you always know who’s on the other side of the question.',
    Form: VisibilityQuestion,
    photo: () => ({
      image: PANEL_PHOTOS.room,
      caption: {
        eyebrow: 'However you answer',
        line: 'Plenty of members stay private for months before they’re ready. Both answers are ordinary here.',
      },
    }),
  },
  {
    id: 6,
    eyebrow: 'Your time here',
    question: 'What were you part of?',
    support: 'Optional. This is how people recognise each other — “you were in the Tuesday group too”.',
    Form: ProgramsQuestion,
    photo: (data) => {
      const draft = asDraft(data)
      const era = ERA_STORIES[decadeFor(draft.startYear || '1990')]
      return {
        image: PANEL_PHOTOS.era,
        caption: { eyebrow: era.tag, line: era.body },
      }
    },
  },
  {
    id: 7,
    eyebrow: 'Your account of it',
    question: 'What do you remember?',
    support:
      'There are years in the archive with almost nothing in them. You may be one of very few people who remembers this one.',
    Form: MemoryQuestion,
    photo: (data) => {
      const draft = asDraft(data)
      const quote = ERA_QUOTES[decadeFor(draft.startYear || '1990')]
      return {
        image: PANEL_PHOTOS.voices,
        caption: { eyebrow: quote.tag, line: quote.headline, footnote: quote.body },
      }
    },
  },
]
